import { useEffect } from 'react'
import useAsync, { type AsyncResult } from '../useAsync'
import { Errors, logError } from '@/services/exceptions'
import useSafeInfo from '../useSafeInfo'
import useIntervalCounter from '@/hooks/useIntervalCounter'
import { POLLING_INTERVAL } from '@/config/constants'
import { useMultiWeb3ReadOnly } from '@/hooks/wallets/web3'
import { useAppDispatch } from '@/store'
import { showNotification } from '@/store/notificationsSlice'
import { getSafeContract } from '@/utils/safe-versions'
import type { SafeTransactionData } from '@safe-global/safe-core-sdk-types'
import type { Result } from 'ethers/lib/utils'
import { buildMultisigTxId } from '@/utils/tx-id'
import { mergeChunkedLogs, queryFilterChunked } from '@/utils/chunkedQueryFilter'
import {
  loadTxHistoryScanCache,
  saveTxHistoryScanCache,
  toScannedLog,
  type ScannedExecutionLog,
} from '@/services/tx/txHistoryScanCache'

export type TxHistoryItem = {
  txId: string
  txHash: string
  safeTxHash: string
  timestamp: number
  executor: string
  decodedTxData?: SafeTransactionData
}

export type TxHistory = {
  [txId: string]: TxHistoryItem
}

const REORG_SAFETY_BLOCKS = 64

function parseDecodedTxData(decodedTxData: Result, nonce: number): SafeTransactionData {
  const [to, value, data, operation, safeTxGas, baseGas, gasPrice, gasToken, refundReceiver] = decodedTxData
  return {
    to,
    value,
    data,
    operation,
    safeTxGas,
    baseGas,
    gasPrice,
    gasToken,
    refundReceiver,
    nonce,
  }
}

export const useLoadTxHistory = (): AsyncResult<TxHistory> => {
  const dispatch = useAppDispatch()
  const provider = useMultiWeb3ReadOnly()
  const { safe, safeAddress } = useSafeInfo()
  const { chainId } = safe
  const [pollCount, resetPolling] = useIntervalCounter(POLLING_INTERVAL)

  const [data, error, loading] = useAsync<TxHistory | undefined>(
    async () => {
      if (!safeAddress || !provider) return

      const safeContract = getSafeContract(safeAddress, safe.version, provider)

      if (!safeContract) return

      const latestBlock = await provider.getBlockNumber()
      // Scan incrementally: only the blocks mined since the last (persisted) scan, plus a
      // reorg-safety overlap. Without this, every reload and 15s poll replays the full
      // multi-chunk eth_getLogs scan and rate-limited public RPCs reject the burst.
      const cached = loadTxHistoryScanCache(chainId, safeAddress)
      const fromBlock = cached ? Math.max(cached.upToBlock - REORG_SAFETY_BLOCKS + 1, 0) : 0

      const freshLogs = await queryFilterChunked(safeContract, safeContract.filters.ExecutionSuccess(), {
        fromBlock,
        toBlock: latestBlock,
      })
      const logs = mergeChunkedLogs<ScannedExecutionLog>(cached?.logs ?? [], freshLogs.map(toScannedLog), fromBlock)
      saveTxHistoryScanCache(chainId, safeAddress, { upToBlock: latestBlock, logs })

      let txs = await Promise.all(
        logs.map(async (log, i) => {
          const [timestamp, { executor, decodedTxData }]: [
            number,
            { executor: string; decodedTxData: Result | undefined },
          ] = await Promise.all([
            provider.getBlock(log.blockNumber).then((block) => block.timestamp * 1000),
            provider.getTransaction(log.transactionHash).then((tx) => {
              try {
                const decodedTxData = safeContract.interface.decodeFunctionData('execTransaction', tx.data)
                return { executor: tx.from, decodedTxData }
              } catch (e) {
                return { executor: tx.from, decodedTxData: undefined }
              }
            }),
          ])

          return {
            txId: buildMultisigTxId(safeAddress, log.args.txHash),
            txHash: log.transactionHash,
            safeTxHash: log.args.txHash,
            timestamp,
            executor,
            decodedTxData: decodedTxData ? parseDecodedTxData(decodedTxData, i) : undefined,
          }
        }),
      )

      return txs.reduce((acc, tx) => {
        acc[tx.txId] = tx
        return acc
      }, {} as TxHistory)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [safeAddress, provider, pollCount],
    false,
  )

  // Log errors
  useEffect(() => {
    if (!error) return
    dispatch(
      showNotification({
        message:
          'Error fetching transaction history. If you see this error often, please consider using a more stable RPC URL.',
        groupKey: 'fetch-tx-history-error',
        variant: 'error',
        detailedMessage: error.message,
      }),
    )
    logError(Errors._602, error.message)
  }, [error, dispatch])

  // Reset the counter when safe address/chainId changes
  useEffect(() => {
    resetPolling()
  }, [resetPolling, safeAddress, chainId])

  return [data, error, loading]
}

export default useLoadTxHistory
